import { useEffect, useState } from 'react';
import { Box, Grid, IconButton, Tab, Tabs, styled } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import AccountSettings from '../../components/Settings/Settings';
import BarcodeSettings from '../../components/BarcodeSettings/BarcodeSettings';
import TagsMaster from '../../components/TagsMaster/TagsMaster';
import { TabPanel } from '../../components/Dialog/TabPanel';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchAccountById } from '../../store/accountThunk';

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
  />
))({
  backgroundColor: '#fafafaff',
  borderRadius: '12px',
  marginLeft: '10px',
  padding: '6px',
  minHeight: '48px',
  maxWidth: 'fit-content',
  '& .MuiTabs-flexContainer': {
    gap: '4px',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#a0aec0',
    zIndex: 2,
    minWidth: 'auto',
    height: '36px',
    minHeight: '36px',
    padding: '8px 20px',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '&:hover:not(.Mui-selected)': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      color: '#059669',
    },
    '&.Mui-selected': {
      backgroundColor: '#059669',
      color: '#ffffff',
      fontWeight: 600,
      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.3)',
    },
  },
});

interface TabPanelProps {
  index: number;
  value: number;
}

const StyledTabPanel = styled((props: TabPanelProps) => (
  <TabPanel
    {...props}
  />
))({
  '& .MuiBox-root': {
    backgroundColor: 'transparent !important',
    padding: '0px !important'
  }
});

interface StyledTabProps {
  label: string;
}

const StyledTab = styled((props: StyledTabProps) => (
  <Tab disableRipple {...props} />
))(({ theme }) => ({
  textTransform: 'none',
  fontWeight: theme.typography.fontWeightRegular,
  fontSize: theme.typography.pxToRem(15),
  marginRight: theme.spacing(1),
  color: '#545454',
  '&.Mui-selected': {
    color: '#059669',
  },
  '&.Mui-focusVisible': {
    backgroundColor: 'rgba(100, 95, 228, 0.32)',
  },
}));

/**
 * Standalone Settings page reached from the header gear icon.
 * Hosts the account settings module that previously lived in the
 * "Settings" tab of My Business Information, one section per tab.
 */
const Settings = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { selectedAccount } = useAppSelector(state => state.auth);
  const accountDetails = useAppSelector(state => state.account.accountDetails);
  const crmEnabled = !!selectedAccount?.unofyCrm?.enabled;

  // The settings cards read the account from the store, so make sure it is loaded
  // for the currently selected business (this page has no :accountId param).
  useEffect(() => {
    if (selectedAccount?._id && accountDetails?._id !== selectedAccount._id) {
      dispatch(fetchAccountById(selectedAccount._id));
    }
  }, [selectedAccount?._id, accountDetails?._id, dispatch]);

  return (
    <Grid container p={0} spacing={2}>

      <Grid item xs={12} ml={-1}>
        <Grid container>
          <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p: 2, pl: 3, pt: 4, pb: 3, bgcolor: '#fff' }}>
            <IconButton
              style={{ float: 'left', color: '#545454', backgroundColor: '#f5f5f5', border: 'none', height: 35, width: 35, marginTop: -5, marginRight: 10, marginLeft: -5 }}
              size='small'
              onClick={() => navigate(-1)}
            >
              <NavigateBeforeOutlinedIcon style={{ fontSize: 20 }} />
            </IconButton>
            <h3 style={{ marginTop: -1, fontWeight: 600 }}>Settings</h3>
          </Grid>
        </Grid>
      </Grid>

      {/* pl/pr override the container spacing's padding so the tabs and cards
          line up with the back button in the header band above. */}
      <Grid item xs={12} sx={{ pl: 3.5, pr: 4, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <StyledTabs value={value}
            onChange={handleChange}
            aria-label="settings tabs">
            <StyledTab label="Notifications" />
            <StyledTab label="Default Cash Account" />
            <StyledTab label="Barcode Format" />
            <StyledTab label="Tags" />
            {/* Tabs takes its children as an array — a false here would break the indices,
                so the CRM tab is only appended when the integration is on. */}
            {crmEnabled ? <StyledTab label="CRM" /> : undefined}
          </StyledTabs>
          <StyledTabPanel value={value} index={0} >
            <AccountSettings section="notifications" />
          </StyledTabPanel>
          <StyledTabPanel value={value} index={1} >
            <AccountSettings section="cashSale" />
          </StyledTabPanel>
          <StyledTabPanel value={value} index={2} >
            <BarcodeSettings />
          </StyledTabPanel>
          <StyledTabPanel value={value} index={3} >
            <TagsMaster />
          </StyledTabPanel>
          {crmEnabled && <StyledTabPanel value={value} index={4} >
            <AccountSettings section="crm" />
          </StyledTabPanel>}
        </Box>
      </Grid>

    </Grid>
  );
};

export default Settings;
